/**
 * ResultCard — one search result entry.
 *
 * v2 design refresh:
 *  - Hairline-row layout: no card border, no rounded corners, no shadow.
 *    Uses border-b border-hairline to separate from the next row (Linear / 247Sports pattern).
 *  - Removed left-tier stripe (border-l-4): tier is communicated solely by ScoreBadge.
 *  - School name bumped to text-[17px] for dominant hierarchy.
 *  - Meta line drops to text-xs for clear size hierarchy below the name.
 *  - Padding tightened to px-4 py-3 (from p-4) for list density.
 *
 * Prop API: unchanged from v1 except WI-19 additions.
 *
 * The parent <ul> uses divide-y divide-hairline instead of per-card borders —
 * see SearchPage results list. The bottom border on this element acts as the
 * last-row divider; divide-y removes the top border on all-but-first children,
 * so duplicates are avoided.
 *
 * FR-010 / TC-011: ScoreBadge communicates tier in text + color.
 * Accessibility: bookmark button aria-label includes school name.
 *
 * WI-18: position prop threads active position filter into detail page URL.
 * WI-19: division, region props added so the detail page can reconstruct
 *        the full "← Back to results" URL (AC-4 requires all filter params present).
 *
 * 14d: major prop removed (no backend support; filter removed from UI).
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { SearchResult, serializeFilters, SearchFilters } from '../api/search';
import { ScoreBadge } from './ScoreBadge';
import { FallbackBadge } from './FallbackBadge';
import { Badge } from './ui/Badge';
import { BookmarkIcon as BookmarkOutlineIcon } from '@heroicons/react/24/outline';
import { BookmarkIcon as BookmarkSolidIcon } from '@heroicons/react/24/solid';

// Item 14a: short labels for the winning_position pill displayed in per-position mode.
// Covers all 10 fine slugs plus the 4 coarse roll-ups still returned by the backend.
const POSITION_SLUG_LABELS: Record<string, string> = {
  gk: 'GK', cb: 'CB', lb: 'LB', rb: 'RB',
  cdm: 'CDM', cm: 'CM', cam: 'CAM',
  lw: 'LW', rw: 'RW', st: 'ST',
  def: 'DEF', mid: 'MID', fwd: 'FWD',
};

interface Props {
  result: SearchResult;
  isAuthenticated: boolean;
  isSaved: boolean;
  onSave: (schoolId: string) => void;
  onAuthPrompt: () => void;
  /** Sport slug for the detail page link (e.g. "soccer_mens"). */
  sport?: string;
  /** Target year for the detail page link. */
  targetYear?: number;
  /** WI-18: Active position filter slugs to thread into the detail page URL. */
  position?: string[];
  /** WI-19: Division filter to thread into the detail page URL for back-link reconstruction. */
  division?: string;
  /** WI-19: Region filter to thread into the detail page URL for back-link reconstruction. */
  region?: string;
}

/**
 * TOTAL-variant badge — rendered in SUM mode (zero position chips selected).
 * Same dark-pill style as ScoreBadge; tier color drives accent.
 */
function TotalBadge({ totalScore }: { totalScore: number }) {
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded bg-gray-800 border border-gray-600"
      aria-label={`Total opening score: ${Math.round(totalScore)} out of 400`}
    >
      <span className="text-[11px] font-semibold uppercase tracking-widest text-gray-300">TOTAL</span>
      <span className="text-[22px] font-bold tabular-nums leading-none text-white">
        {Math.round(totalScore)}
      </span>
    </span>
  );
}

export function ResultCard({
  result,
  isAuthenticated,
  isSaved,
  onSave,
  onAuthPrompt,
  sport,
  targetYear,
  position,
  division,
  region,
}: Props) {
  const [saved, setSaved] = useState(isSaved);

  function handleSaveClick(e: React.MouseEvent) {
    e.preventDefault();
    if (!isAuthenticated) {
      onAuthPrompt();
      return;
    }
    setSaved((prev) => !prev);
    onSave(result.school_id);
  }

  // WI-19: Build detail URL using serializeFilters (single source of truth).
  // This ensures all active filter params (sport, position, division, region, target_year)
  // are present in the detail URL so SchoolDetailPage can reconstruct the back link.
  // WI-43: SearchFilters.division is now string[] — wrap the single-string prop.
  const filterParams: SearchFilters = {
    sport,
    position,
    division: division ? [division] : undefined,
    region,
    target_year: targetYear,
  };
  const paramString = serializeFilters(filterParams);
  const detailUrl = `/schools/${result.school_slug}${paramString ? `?${paramString}` : ''}`;

  // Item 14a: dispatch on which mode-specific fields are present.
  // Per-position mode: `winning_position` set, `total_score` absent.
  // SUM mode: `total_score` set, `winning_position` absent.
  const hasPositionMode = result.winning_position !== undefined && result.score !== undefined;
  const hasTotalMode = result.total_score !== undefined;
  const winningLabel = result.winning_position
    ? POSITION_SLUG_LABELS[result.winning_position] ?? result.winning_position.toUpperCase()
    : undefined;
  const ariaScore = result.score ?? result.total_score;

  return (
    <article
      className="px-4 py-3 focus-within:ring-2 focus-within:ring-brand-primary"
      aria-label={
        ariaScore !== undefined
          ? `${result.school_name}, score ${ariaScore.toFixed(0)}`
          : result.school_name
      }
    >
      <div className="flex items-center justify-between gap-2">
        {/* School info — name is a link to the detail page */}
        <div className="flex-1 min-w-0">
          <h2 className="text-[17px] font-semibold text-gray-900 leading-tight truncate">
            <Link
              to={detailUrl}
              className="hover:text-brand-primary-dk hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary rounded"
              aria-label={`View roster detail for ${result.school_name}`}
            >
              {result.school_name}
            </Link>
          </h2>
          {/* WCAG override: text-gray-500 on surface-base = 4.44:1 FAIL AA; using text-gray-600 = 6.94:1 PASS */}
          <p className="text-xs text-gray-600 mt-0.5 flex items-center gap-1.5">
            <Badge className="mr-0.5">
              {result.division.toUpperCase().replace(/_/g, ' ')}
            </Badge>
            {result.region} &middot; {result.state}
          </p>
        </div>

        {/* Score + save */}
        <div className="flex items-center gap-2 flex-shrink-0">
          {hasPositionMode && result.score !== undefined && (
            <div className="flex flex-col items-end gap-0.5">
              <div className="flex items-center gap-1.5">
                {/* WI-26 three-state badge rendering, restyled by WI-32:
                    State 1 (has_data=true): dark-pill ScoreBadge — position label
                      stacked above score (the prior external winningLabel chip is
                      now redundant and removed).
                    State 2 (has_data=false, fallback present): muted FallbackBadge
                      with parent-position label stacked above score.
                    State 3 (has_data=false, no fallback): ScoreBadge at LOW tier. */}
                {result.has_data === false && result.fallback_score ? (
                  <FallbackBadge
                    score={result.fallback_score.score}
                    parentPosition={result.fallback_score.position}
                  />
                ) : (
                  <ScoreBadge
                    score={result.score}
                    tier={result.tier}
                    position={winningLabel}
                  />
                )}
              </div>
              {/* WI-26: secondary fallback label — surfaces context per-row since the
                  result list has no nearby legend (unlike the hero live-preview pane). */}
              {result.has_data === false && result.fallback_score && (
                <p
                  className="text-xs text-gray-500 mt-0.5 flex flex-col items-start"
                  aria-hidden="true"
                >
                  <span>{result.fallback_score.position_name} score · no {winningLabel ?? result.winning_position} data</span>
                </p>
              )}
            </div>
          )}
          {!hasPositionMode && hasTotalMode && result.total_score !== undefined && (
            <TotalBadge totalScore={result.total_score} />
          )}
          {/* C1: Heroicons bookmark replaces Unicode star for consistent cross-platform rendering */}
          <button
            type="button"
            onClick={handleSaveClick}
            className={`min-h-[44px] min-w-[44px] flex items-center justify-center rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary motion-safe:transition-all motion-safe:duration-150 ${
              saved ? 'text-brand-primary' : 'text-gray-400 hover:text-brand-primary'
            }`}
            aria-label={
              isAuthenticated
                ? saved
                  ? `Remove ${result.school_name} from watchlist`
                  : `Save ${result.school_name} to watchlist`
                : `Log in to save ${result.school_name}`
            }
          >
            {saved
              ? <BookmarkSolidIcon className="w-5 h-5" aria-hidden="true" />
              : <BookmarkOutlineIcon className="w-5 h-5" aria-hidden="true" />
            }
          </button>
        </div>
      </div>
    </article>
  );
}
